// MODULES
import React from "react";
import PropTypes from "prop-types";

// HELPERS
import StackLayout from "../../../../components/layouts/StackLayout/StackLayout";
import Button from "../../../../components/Button/Button";
import EditableField from "../EditableField/EditableField";
import FieldsCollection from "../../../../components/FieldsCollection/FieldsCollection";
import Checkbox from "../../../../components/Checkbox/Checkbox";
import Validator from "../../../../classes/Validator";
import i18n from "@helpers/i18n";

const MAX_COMPANY_REPS = 10;

export default class CompanyReps extends React.PureComponent {
	static propTypes = {
		orgNr: PropTypes.string,
		firma: PropTypes.string,
		ceo: PropTypes.object,
		board: PropTypes.array,
		onRepsUpdated: PropTypes.func
	};

	state = {
		orgNr: "",
		firma: "",
		representatives: [{
			first_name: "",
			last_name: "",
			ssn: "",
			email: "",
			role: []
		}],
		filled: false
	};


	/**
	 * Load reps from lookup data sent as a property to the component. If no lookup data is provided
	 * we show an empty row.
	 */
	componentDidUpdate(prevProps) {
		let shouldUpdateState = false;

		if (prevProps.orgNr !== this.props.orgNr ||
			prevProps.board.length !== this.props.board.length) {
			shouldUpdateState = true;
		}
		else if (prevProps.board.length === this.props.board.length) {
			for (let i = 0; i < this.props.board.length; i++) {
				if (this.props.board[i] == null) {
					continue;
				}

				if (this.props.board[i].ssn !== prevProps.board[i].ssn) {
					shouldUpdateState = true;
					break;
				}
			}
		}

		if (shouldUpdateState) {
			const reps = [];
			let hasSavedCeo = false;

			for (let i = 0; i < this.props.board.length; i++) {

				if (this.props.board[i] == null) {
					continue;
				}

				let rep = {
					first_name: this.props.board[i].first_name,
					last_name: this.props.board[i].last_name,
					ssn: this.props.board[i].ssn,
					role: ["board"]
				};

				if (this.props.ceo.ssn === this.props.board[i].ssn) {
					hasSavedCeo = true;
					rep.role = ["board", "ceo"];
				}

				reps.push(rep);
			}

			if (!hasSavedCeo && this.props.ceo !== null && this.props.ceo['first_name'] !== undefined) {
				reps.unshift({
					first_name: this.props.ceo.first_name,
					last_name: this.props.ceo.last_name,
					ssn: this.props.ceo.ssn,
					email: "",
					role: ["ceo"]
				});
			}

			if (reps.length === 0) (
				reps.push({
					first_name: "",
					last_name: "",
					ssn: "",
					email: "",
					role: []
				})
			);

			this.setState({
				orgNr: this.props.orgNr,
				firma: this.props.firma,
				representatives: reps
			}, () => {
				this.props.onRepsUpdated(this.state.representatives);
			});
		}
	}

	render() {
		const {representatives} = this.state;

		let hasAtLeastOneSignatory = false;
		representatives.forEach(function (rep) {
			if (rep.role.includes("signatory")) {
				hasAtLeastOneSignatory = true;
			}
		});

		return (
			<div className="companyReps">
				{this.state.firma !== "" && (
					<React.Fragment>
						<h4 style={{margin: "20px 0 0 0", borderTop: "2px solid #f4F5f7", width: "100%", paddingTop: "20px"}}>Bolagets Firmateckning</h4>

						<p style={{margin: "10px 0 0 0"}}>
							{this.state.firma || "-"}
						</p>
					</React.Fragment>
				)}

				<h4 style={{margin: "20px 0 10px 0", borderTop: "2px solid #f4F5f7", width: "100%", paddingTop: "40px"}}>
					{i18n("company_reps", "header")}
				</h4>

				<div className="reps">
					{representatives.map((rep, i) => (
						<div key={i} className="rep">
							<FieldsCollection
								fieldsPerRow={3}
								fields={[
									{
										render: (
											<EditableField
												name={"company_rep_first_name_" + i}
												label={i18n("company_reps", "first_name")}
												value={rep.first_name}
												valid={rep.first_name.trim().length !== 0}
												className={rep.first_name.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												required
											/>
										),
									},
									{
										render: (
											<EditableField
												name={"company_rep_last_name_" + i}
												label={i18n("company_reps", "last_name")}
												value={rep.last_name}
												valid={rep.last_name.trim().length !== 0}
												className={rep.last_name.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												required
											/>
										)
									},
									{
										render: (
											<EditableField
												name={"company_rep_ssn_" + i}
												label={i18n("company_reps", "ssn")}
												value={rep.ssn}
												className={rep.ssn.trim().length === 0 ? "empty" : ""}
												onChange={this._onFieldChange}
												required
											/>
										),
									}
								]}
							/>

							<div className="type">
								<Checkbox
									name={"company_rep_type_signatory_" + i}
									label={i18n("company_reps", "role_signatory")}
									value="yes"
									checked={rep.role.includes("signatory")}
									onChange={this._onTypeChange.bind(this, rep.ssn, "signatory")}
								/>
								<Checkbox
									name={"company_rep_type_ceo_" + i}
									label={i18n("company_reps", "role_ceo")}
									value="yes"
									checked={rep.role.includes("ceo")}
									onChange={this._onTypeChange.bind(this, rep.ssn, "ceo")}
								/>

								<Checkbox
									name={"company_rep_type_styrelse_" + i}
									label={i18n("company_reps", "role_board")}
									value="yes"
									checked={rep.role.includes("board")}
									onChange={this._onTypeChange.bind(this, rep.ssn, "board")}
								/>
							</div>

							{rep.role.includes("signatory") && (
								<div className="rep-sign">
									<p>{i18n("customer_info", "email_info")}</p>
									<FieldsCollection
										fieldsPerRow={2}
										fields={[
											{
												render: (
													<EditableField
														name={"company_rep_email_" + i}
														label={i18n("customer_info", "email")}
														value={rep.email}
														valid={Validator.validateEmail(rep.email)}
														className={rep.email === undefined || rep.email.length === 0 ? "empty" : ""}
														onChange={this._onFieldChange}
														required
													/>
												),
											}
										]}
									/>
								</div>
							)}
						</div>
					))}
				</div>

				<StackLayout style={{width: "60%"}}>
					<Button
						size="small"
						label={i18n("company_reps", "remove_last")}
						appearance={this.state.representatives.length <= 1 ? "medium" : "danger"}
						disabled={this.state.representatives.length <= 1}
						onClick={this._removeLastRep.bind(this)}
					/>

					<Button
						size="small"
						label={i18n("company_reps", "add_more")}
						appearance={this.state.representatives.length >= MAX_COMPANY_REPS ? "medium" : "primary"}
						disabled={this.state.representatives.length >= MAX_COMPANY_REPS}
						onClick={this._addEmptyRep.bind(this)}
						filled
					/>
				</StackLayout>

				{!hasAtLeastOneSignatory && (
					<p style={{margin: "10px 0 0 0", fontWeight: "bold", color: "red", fontStyle: "normal"}}>
						{i18n("company_reps", "hint_select_one")}
					</p>
				)}
			</div>
		);
	}

	_addEmptyRep = () => {
		let emptyRow = {
			first_name: "",
			last_name: "",
			ssn: "",
			email: "",
			role: []
		};

		this.setState({
			representatives: [...this.state.representatives, emptyRow]
		}, () => {
			this.props.onRepsUpdated(this.state.representatives);
		});
	};

	_removeLastRep = () => {
		const repsList = [...this.state.representatives];

		if (repsList.length > 0) {
			repsList.pop();
		}

		this.setState({
			representatives: repsList
		}, () => {
			this.props.onRepsUpdated(this.state.representatives);
		});
	};

	_onFieldChange = (name, value) => {
		let index = name.substring(name.lastIndexOf("_") + 1);
		let field = name.replace("company_rep_", "");
		field = field.substring(0, field.lastIndexOf("_"));

		const repsList = [...this.state.representatives];
		repsList[index][field] = value;

		this.setState({
			representatives: repsList
		}, () => {
			this.props.onRepsUpdated(this.state.representatives);
		});
	};

	_onTypeChange = (ssn, type) => {
		const repsList = [...this.state.representatives];

		for (let i = 0; i < repsList.length; i++) {
			let rep = repsList[i];
			if (rep.ssn === ssn) {
				if (!rep.role.includes(type)) {
					rep.role.push(type);
				}
				else {
					rep.role = rep.role.filter(item => item !== type);
				}
			}
		}

		this.setState({
			representatives: repsList
		}, () => {
			this.props.onRepsUpdated(this.state.representatives);
		});
	};
}


// WEBPACK FOOTER //
// ./src/routes/Cob/components/CompanyReps/CompanyReps.js